import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { Assessment, Save, Publish } from "@mui/icons-material";
import api from "../../api";

export default function ExamResultManagement() {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [open, setOpen] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const schoolId = localStorage.getItem("school_id");

  const fetchExams = async () => {
    try {
      const res = await api.get(`/exam/list?school_id=${schoolId}`);
      const data = res.data;

      // ✅ Safe checks for null/undefined data
      if (data && data.status && data.data) {
        if (Array.isArray(data.data)) {
          setExams(data.data);
        } else if (typeof data.data === "object") {
          setExams([data.data]);
        } else {
          setExams([]);
        }
      } else {
        setExams([]);
      }
    } catch (err) {
      console.error("Error fetching exams:", err);
      setExams([]); // ✅ fallback
    }
  };

  const fetchAssignedApplicants = async (exam) => {
    setLoading(true);
    setSelectedExam(exam);
    setOpen(true);

    try {
      const res = await api.get(
        `/exam/assigned_list?exam_id=${exam.id}&school_id=${schoolId}`
      );
      const data = res.data;

      // ✅ check safe structure
      if (data && data.status && Array.isArray(data.data)) {
        const list = data.data.map((a) => ({
          ...a,
          application_id: a.application_id || "",
          marks_obtained: a.marks_obtained || "",
          remarks: a.remarks || "",
          name: a.name || "—",
          class_applied: a.class_applied || "—",
          mobile_no: a.mobile_no || "—",
          photo: a.photo || "",
        }));
        setApplicants(list);
      } else {
        setApplicants([]); // ✅ fallback empty list
      }
    } catch (err) {
      console.error("Error fetching assigned applicants:", err);
      setApplicants([]); // ✅ no crash if API fails
    } finally {
      setLoading(false);
    }
  };


  // ✅ Handle marks/remarks change (only if editable)
  const handleChange = (application_id, field, value) => {
    if (selectedExam?.is_published === 1) return; // 🔒 Prevent edit if published
    const updated = applicants.map((a) =>
      a.application_id === application_id ? { ...a, [field]: value } : a
    );
    setApplicants(updated);
  };

  // ✅ Save Results (only if not published)
  const handleSaveResults = async () => {
    if (!selectedExam || selectedExam.is_published === 1) {
      setMsg({ type: "warning", text: "This result is already published." });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("exam_id", selectedExam.id);
      formData.append("school_id", schoolId);

      const resultsArray = applicants.map((a) => ({
        application_id: a.application_id,
        marks: a.marks_obtained || 0,
        remarks: a.remarks || "",
      }));

      formData.append("results", JSON.stringify(resultsArray));

      const res = await api.post("/exam_result/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status) {
        setMsg({ type: "success", text: "Results saved successfully." });
      } else {
        setMsg({ type: "error", text: res.data.message || "Save failed." });
      }
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "Error saving results." });
    }
  };

  // ✅ Publish Results
  const handlePublishResults = async () => {
    if (!selectedExam) return;
    if (!window.confirm("Are you sure you want to publish this result?")) return;

    try {
      const res = await api.post("/exam_result/publish", {
        exam_id: selectedExam.id,
        school_id: schoolId,
      });

      if (res.data.status) {
        setMsg({ type: "success", text: "Results published successfully." });
        setOpen(false);
        fetchExams(); // refresh list
      } else {
        setMsg({
          type: "error",
          text: res.data.message || "Failed to publish results.",
        });
      }
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "Error publishing results." });
    }
  };

  useEffect(() => {
    fetchExams();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          🧮 Exam Result Management
        </Typography>
      </Box>

      {/* Exam List */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Exam Name</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Venue</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exams.map((exam) => (
              <TableRow key={exam.id}>
                <TableCell>{exam.exam_name}</TableCell>
                <TableCell>{exam.exam_date}</TableCell>
                <TableCell>{exam.exam_time}</TableCell>
                <TableCell>{exam.venue}</TableCell>
                <TableCell>
                  {exam.is_published === "1" ? (
                    <Typography color="green">Published</Typography>
                  ) : (
                    <Typography color="orange">Draft</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => fetchAssignedApplicants(exam)}
                  >
                    <Assessment />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Result Entry Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>
          {selectedExam?.is_published === "1"
            ? `View Published Results — ${selectedExam?.exam_name}`
            : `Enter Results — ${selectedExam?.exam_name}`}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
              <Typography>Loading applicants...</Typography>
            </Box>
          ) : applicants.length > 0 ? (
            <Paper sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Photo</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Marks</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applicants.map((a) => {
                    const marks = Number(a.marks_obtained || 0);
                    const passMarks = Number(selectedExam?.pass_marks || 0);
                    const result = marks >= passMarks ? "Passed" : "Failed";

                    return (
                      <TableRow key={a.application_id}>
                        <TableCell>
                          <Avatar
                            src={a.photo ? `https://dgps.co.in/${a.photo}` : ""}
                            sx={{ width: 40, height: 40 }}
                          />
                        </TableCell>
                        <TableCell>{a.name}</TableCell>
                        <TableCell>{a.class_applied}</TableCell>
                        <TableCell>{a.mobile_no}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={a.marks_obtained}
                            onChange={(e) =>
                              handleChange(a.application_id, "marks_obtained", e.target.value)
                            }
                            sx={{ width: 100 }}
                            disabled={selectedExam?.is_published === "1"}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: result === "Passed" ? "green" : "red",
                          }}
                        >
                          {result}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={a.remarks}
                            onChange={(e) =>
                              handleChange(a.application_id, "remarks", e.target.value)
                            }
                            sx={{ width: 150 }}
                            disabled={selectedExam?.is_published === "1"}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Typography sx={{ mt: 3 }}>No applicants found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {selectedExam?.is_published !== "1" && (
            <>
              <Button variant="outlined" startIcon={<Save />} onClick={handleSaveResults}>
                Save
              </Button>
              <Button variant="contained" startIcon={<Publish />} onClick={handlePublishResults}>
                Publish
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!msg}
        autoHideDuration={3000}
        onClose={() => setMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {msg && (
          <Alert severity={msg.type} sx={{ width: "100%" }}>
            {msg.text}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
